import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import RecipeSummaryCardImage from "../components/RecipeSummaryCardImage";
import SubscribeBanner from "../components/SubscribeBanner";

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'favourites', label: 'Favourites' },
  { key: 'highRated', label: 'Top rated' },
  //{ key: 'quick', label: 'Quick Meals' },
];
const SECONDARYFILTERS = [
  { key: 'quick', label: 'Quick Meals' },
];

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [secondaryFilter, setSecondaryFilter] = useState('');
  const navigate = useNavigate();
  const LIMIT = 10;

  // Fetch recipes for a specific page: replaces or appends based on page number
  const fetchRecipes = useCallback(
    async (pageToFetch = 1) => {
      setLoading(true);
      try {
        const params = { q: searchTerm, filter, secondaryFilter, page: pageToFetch, limit: LIMIT };
        const res = await apiClient.get('/recipes/search', { params });
        const { data, hasNextPage: next, page: current } = res.data;

        if (pageToFetch === 1) {
          setRecipes(data);
        } else {
          setRecipes(prev => [...prev, ...data]);
        }
        setHasNextPage(next);
        setPage(current);
      } catch (err) {
        console.error('Failed to fetch recipes', err);
      } finally {
        setLoading(false);
      }
    },
    [filter, secondaryFilter, searchTerm]
  );

  // Initial load and reset on filter/search change
  useEffect(() => {
    fetchRecipes(1);
  }, [filter, secondaryFilter, searchTerm]);

  const handleLoadMore = () => {
    if (!loading && hasNextPage) {
      fetchRecipes(page + 1);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 mt-8 mb-4">
      <SubscribeBanner />
      <h1 className="text-2xl font-bold mt-4 mb-4">My Recipe Book</h1>

      {/* Search Input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search recipes..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded"
        />
      </div>

      {/* Filter Buttons */}
      <div className="flex items-center space-x-4 mb-6">
      {/* Primary Filters */}
      <div className="flex space-x-2">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium focus:outline-none
              ${filter === f.key ? 'bg-sage text-white hover:bg-sagedark' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            {f.label}
          </button>
        ))}
      </div>
        {/* Divider */}
          <div className="h-6 w-px bg-gray-300" />

        {/* Secondary Filters */}
        <div className="flex space-x-2">
        {SECONDARYFILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setSecondaryFilter(prev => (prev === f.key ? null : f.key))}
            className={`px-4 py-2 rounded-full text-sm font-medium focus:outline-none
              ${secondaryFilter === f.key ? 'bg-sage text-white hover:bg-sagedark' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      </div>

      {/* Recipe List */}
      {recipes.length === 0 && !loading ? (
        
          searchTerm ? (
            <div className="text-center space-y-4">
              <p className="text-gray-600">
                We can't find any of your recipes matching that search.
              </p>
              <p className="text-gray-600 text-sm">
                Looking to try something new? Chat with Sous to get personalised recipes or upload an image to turn ingredients into recipes.
              </p>
              <button
                onClick={() => navigate("/chat")}
                className="w-full bg-sage border border-sage text-white text-sm py-2 px-4 rounded-3xl hover:bg-sagedark"
              >
                Discover delicious new recipes
              </button>
            </div>
          ) : (
            <p className="text-gray-600 text-center">You haven’t saved any recipes yet.</p>
          )

      ) : (
        <div className="space-y-4">
          {recipes.map(recipe => (
            <RecipeSummaryCardImage key={recipe._id} recipe={recipe} />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasNextPage && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="px-6 py-2 bg-sage text-white rounded hover:bg-sagedark disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
